const EW_FOCUS_BOUNDARY = Object.freeze({firstIndex: 0, step: 1, fallbackTabIndex: -1});

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  ).filter(element => element.tabIndex >= EW_FOCUS_BOUNDARY.firstIndex &&
    !element.matches(':disabled') &&
    !element.closest('[hidden],[inert],[aria-hidden="true"]') &&
    element.getClientRects().length &&
    getComputedStyle(element).visibility !== 'hidden');
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  removeTrapFocus();
  const fallbackTab = String(EW_FOCUS_BOUNDARY.fallbackTabIndex);
  const addedTabIndex = !container.hasAttribute('tabindex');
  if (addedTabIndex) container.setAttribute('tabindex', fallbackTab);
  trapFocusHandlers.cleanup = () => {
    if (addedTabIndex && container.getAttribute('tabindex') === fallbackTab) container.removeAttribute('tabindex');
  };
  let lastFocused;
  trapFocusHandlers.focusin = event => {
    if (container.contains(event.target)) lastFocused = event.target;
  };
  document.addEventListener('focusin', trapFocusHandlers.focusin);
  trapFocusHandlers.keydown = event => {
    if (event.code.toUpperCase() !== 'TAB') return;
    const active = document.activeElement;
    const lostFocus = lastFocused && !container.contains(lastFocused) && (active === document.body || active === document.documentElement);
    if (!container.contains(active) && !lostFocus) return;
    const elements = getFocusableElements(container);
    const first = elements[EW_FOCUS_BOUNDARY.firstIndex];
    const last = elements[elements.length - EW_FOCUS_BOUNDARY.step];
    if (!first) { event.preventDefault(); container.focus(); return; }
    const hiddenFocus = active !== container && !elements.includes(active);
    if (lostFocus || hiddenFocus) {
      event.preventDefault(); (event.shiftKey ? last : first).focus();
    } else if (event.shiftKey && (active === container || active === first)) {
      event.preventDefault(); last.focus();
    } else if (!event.shiftKey && (active === last || active === container)) {
      event.preventDefault(); first.focus();
    }
  };
  document.addEventListener('keydown', trapFocusHandlers.keydown);
  const visible = elementToFocus?.getClientRects().length &&
    !elementToFocus.closest('[hidden],[inert],[aria-hidden="true"]') &&
    !elementToFocus.matches(':disabled') && getComputedStyle(elementToFocus).visibility !== 'hidden';
  const target = visible ? elementToFocus : (getFocusableElements(container)[EW_FOCUS_BOUNDARY.firstIndex] || container);
  target.focus();
  if (target.tagName === 'INPUT' && ['search', 'text', 'email', 'url'].includes(target.type) && target.value) {
    target.setSelectionRange(EW_FOCUS_BOUNDARY.firstIndex, target.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  trapFocusHandlers.cleanup?.();
  trapFocusHandlers.cleanup = null;
  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

const EW_DRAWER_MOTION=Object.freeze({durationMs:400,submenuDelayMs:100,millisecondsPerSecond:1000,borderOffset:1,zero:0});
class MenuDrawer extends HTMLElement {
 constructor(){super();this.mainDetailsToggle=this.querySelector('details');this.closeStates=new Map();this.openTimers=new Map();this.addEventListener('keyup',this.onKeyUp.bind(this));this.addEventListener('focusout',this.onFocusOut.bind(this));this.bindEvents()}
 bindEvents(){
  this.boundSummaries ||= new WeakSet();this.boundButtons ||= new WeakSet();
  this.querySelectorAll('summary').forEach(summary=>{if(this.boundSummaries.has(summary))return;this.boundSummaries.add(summary);summary.addEventListener('click',this.onSummaryClick.bind(this))});
  this.querySelectorAll('button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)').forEach(button=>{if(this.boundButtons.has(button))return;this.boundButtons.add(button);button.addEventListener('click',this.onCloseButtonClick.bind(this))});
 }
 prepareOpen(details){
  const previous=this.closeStates.get(details);if(previous){clearTimeout(previous.timer);this.closeStates.delete(details)}
  clearTimeout(this.openTimers.get(details));this.openTimers.delete(details);delete details.dataset.ewClosing;details.open=true;
 }
 onKeyUp(event){if(event.code.toUpperCase()!=='ESCAPE')return;const details=event.target.closest('details[open]');if(!details)return;details===this.mainDetailsToggle?this.closeMenuDrawer(event,details.querySelector('summary')):this.closeSubmenu(details)}
 onSummaryClick(event){
  const summary=event.currentTarget,details=summary.parentNode,parent=details.closest('.has-submenu');
  const open=details.open&&!details.dataset.ewClosing;
  if(details===this.mainDetailsToggle){event.preventDefault();open?this.closeMenuDrawer(event,summary):this.openMenuDrawer(summary);if(matchMedia('(max-width: 990px)').matches)document.documentElement.style.setProperty('--viewport-height',`${innerHeight}px`);return;}
  if(open){event.preventDefault();this.closeSubmenu(details);return;}
  event.preventDefault();this.prepareOpen(details);summary.setAttribute('aria-expanded','true');
  const finish=()=>{this.openTimers.delete(details);if(!details.open||details.dataset.ewClosing)return;details.classList.add('menu-opening');parent?.classList.add('submenu-open');trapFocus(summary.nextElementSibling,details.querySelector('button'))};
  if(matchMedia('(prefers-reduced-motion: reduce)').matches)finish();else this.openTimers.set(details,setTimeout(finish,EW_DRAWER_MOTION.submenuDelayMs));
 }
 openMenuDrawer(summary){
  this.prepareOpen(this.mainDetailsToggle);summary.setAttribute('aria-expanded','true');document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);trapFocus(this.mainDetailsToggle,summary);
  const details=this.mainDetailsToggle;this.openTimers.set(details,setTimeout(()=>{this.openTimers.delete(details);if(details.open&&!details.dataset.ewClosing)details.classList.add('menu-opening')}));
 }
 closeMenuDrawer(event,elementToFocus=false){
  const details=this.mainDetailsToggle;if(!details.open||details.dataset.ewClosing)return;
  clearTimeout(this.openTimers.get(details));this.openTimers.delete(details);details.classList.remove('menu-opening');details.dataset.ewClosing='true';details.querySelector('summary')?.setAttribute('aria-expanded','false');
  details.querySelectorAll('details').forEach(child=>{clearTimeout(this.openTimers.get(child));this.openTimers.delete(child);const state=this.closeStates.get(child);if(state)clearTimeout(state.timer);this.closeStates.delete(child);child.removeAttribute('open');child.classList.remove('menu-opening');delete child.dataset.ewClosing});
  details.querySelectorAll('.submenu-open').forEach(node=>node.classList.remove('submenu-open'));
  this.closeAnimation(details,()=>{document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);removeTrapFocus(elementToFocus||details.querySelector('summary'));this.onDrawerClosed?.()});
 }
 onFocusOut(){setTimeout(()=>{if(this.mainDetailsToggle.open&&!this.mainDetailsToggle.contains(document.activeElement)&&!this.mainDetailsToggle.dataset.ewClosing)this.closeMenuDrawer(new Event('focusout'))})}
 onCloseButtonClick(event){
  if(event.currentTarget.closest('.ew-appearance-button,.ew-theme-toggle,.ew-share-button,.ew-share-popover'))return;
  const details=event.currentTarget.closest('details');if(!details)return;
  details===this.mainDetailsToggle?this.closeMenuDrawer(event,details.querySelector('summary')):this.closeSubmenu(details);
 }
 closeSubmenu(details){
  if(!details||details.dataset.ewClosing)return;
  clearTimeout(this.openTimers.get(details));this.openTimers.delete(details);details.dataset.ewClosing='true';details.closest('.submenu-open')?.classList.remove('submenu-open');details.classList.remove('menu-opening');details.querySelector('summary')?.setAttribute('aria-expanded','false');
  this.closeAnimation(details,()=>{const parent=details.closest('details[open]');if(parent)trapFocus(parent,details.querySelector('summary'));else removeTrapFocus(details.querySelector('summary'))});
 }
 closeAnimation(details,complete){
  const previous=this.closeStates.get(details);if(previous)clearTimeout(previous.timer);
  const panel=details===this.mainDetailsToggle?details.querySelector('.menu-drawer'):details.querySelector('.menu-drawer__submenu');
  const styles=panel?getComputedStyle(panel):null;
  const parse=value=>{const n=parseFloat(value);return Number.isFinite(n)?n*(value.trim().endsWith('ms')?1:EW_DRAWER_MOTION.millisecondsPerSecond):EW_DRAWER_MOTION.zero};
  const durations=styles?.transitionDuration.split(',').map(parse)||[],delays=styles?.transitionDelay.split(',').map(parse)||[];
  const duration=matchMedia('(prefers-reduced-motion: reduce)').matches?EW_DRAWER_MOTION.zero:Math.max(EW_DRAWER_MOTION.durationMs,...durations.map((n,i)=>n+(delays[i%delays.length]||EW_DRAWER_MOTION.zero)));
  const state={};this.closeStates.set(details,state);
  const finish=()=>{if(this.closeStates.get(details)!==state)return;this.closeStates.delete(details);details.open=false;delete details.dataset.ewClosing;complete?.()};
  if(duration===EW_DRAWER_MOTION.zero)finish();else state.timer=setTimeout(finish,duration);
 }
 disconnectedCallback(){for(const state of this.closeStates.values())clearTimeout(state.timer);for(const timer of this.openTimers.values())clearTimeout(timer);document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`)}
}
customElements.define('menu-drawer',MenuDrawer);
class HeaderDrawer extends MenuDrawer {
 openMenuDrawer(summary){
  this.header=this.header||document.querySelector('.section-header');this.borderOffset=this.closest('.header-wrapper')?.classList.contains('header-wrapper--border-bottom')?EW_DRAWER_MOTION.borderOffset:EW_DRAWER_MOTION.zero;
  if(this.header){document.documentElement.style.setProperty('--header-bottom-position',`${Math.round(this.header.getBoundingClientRect().bottom-this.borderOffset)}px`);this.header.classList.add('menu-open')}
  window.addEventListener('resize',this.onResize);super.openMenuDrawer(summary);
 }
 onDrawerClosed(){this.header?.classList.remove('menu-open');window.removeEventListener('resize',this.onResize)}
 disconnectedCallback(){super.disconnectedCallback();this.onDrawerClosed()}
 onResize=()=>{if(this.header)document.documentElement.style.setProperty('--header-bottom-position',`${Math.round(this.header.getBoundingClientRect().bottom-this.borderOffset)}px`);document.documentElement.style.setProperty('--viewport-height',`${innerHeight}px`)};
}
customElements.define('header-drawer',HeaderDrawer);


class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        const playPromise = deferredElement.play();
        if (playPromise) playPromise.catch(() => {});
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}

/* Animate the product detail accordions without changing native details semantics. */
(() => {
  const config = Object.freeze({selector:'.product__description .ew-product-accordion',duration:460,enterDuration:340,exitDuration:240,lift:'8px',easing:'cubic-bezier(.22, 1, .36, 1)',millisecondsPerSecond:1000,heightTolerance:1});
  const active = new Map();
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const timing = (styles,name,fallback) => {
    const raw=styles.getPropertyValue(name).trim(),number=parseFloat(raw);
    return Number.isFinite(number)?(raw.endsWith('ms')?number:raw.endsWith('s')?number*config.millisecondsPerSecond:fallback):fallback;
  };
  const clean = (details,state) => {
    if(active.get(details)!==state)return;
    state.observer?.disconnect();state.height?.cancel();state.body?.cancel();
    details.open=state.expanding;details.style.height='';details.style.overflow='';
    details.classList.remove('ew-accordion-collapsing');active.delete(details);
  };
  const animate = (details,summary,content,expanding) => {
    const previous=active.get(details),styles=getComputedStyle(details),bodyStyles=getComputedStyle(content);
    const start=details.getBoundingClientRect().height;
    const opacity=previous?bodyStyles.opacity:expanding?'0':'1';
    const lift=styles.getPropertyValue('--ew-accordion-lift').trim()||config.lift;
    const transform=previous?bodyStyles.transform:expanding?'translateY('+lift+')':'none';
    previous?.observer?.disconnect();previous?.height?.cancel();previous?.body?.cancel();
    const state={expanding};active.set(details,state);
    if(reduced.matches||typeof details.animate!=='function'){clean(details,state);return;}
    details.open=true;details.classList.toggle('ew-accordion-collapsing',!expanding);
    const duration=timing(styles,'--ew-accordion-duration',config.duration);
    const bodyDuration=timing(styles,expanding?'--ew-accordion-enter-duration':'--ew-accordion-exit-duration',expanding?config.enterDuration:config.exitDuration);
    const easing=styles.getPropertyValue('--ew-accordion-ease').trim()||config.easing;
    const target=()=>summary.getBoundingClientRect().height+(parseFloat(styles.borderTopWidth)||0)+(parseFloat(styles.borderBottomWidth)||0)+(expanding?content.getBoundingClientRect().height:0);
    state.target=target();details.style.height=start+'px';details.style.overflow='hidden';
    const heightTo=(from,to)=>{
      state.height?.cancel();
      state.height=details.animate([{height:from+'px'},{height:to+'px'}],{duration,easing,fill:'forwards'});
      const currentHeight=state.height;
      currentHeight.addEventListener('finish',()=>{if(state.height===currentHeight)clean(details,state)},{once:true});
    };
    heightTo(start,state.target);
    state.body=content.animate([{opacity,transform},{opacity:expanding?'1':'0',transform:expanding?'none':'translateY(-'+lift+')'}],{duration:bodyDuration,easing,fill:'forwards'});
    if(typeof ResizeObserver==='function'){
      state.observer=new ResizeObserver(()=>{
        if(active.get(details)!==state)return;
        const next=target();if(Math.abs(next-state.target)<=config.heightTolerance)return;
        const from=details.getBoundingClientRect().height;state.target=next;heightTo(from,next);
      });state.observer.observe(content);state.observer.observe(summary);
    }
  };
  document.addEventListener('click',event=>{
    const summary=event.target instanceof Element?event.target.closest(config.selector+' > summary'):null;
    if(!summary)return;event.preventDefault();
    const details=summary.parentElement,state=active.get(details),expanding=state?!state.expanding:!details.open;
    const content=details.querySelector(':scope > div');
    if(content)animate(details,summary,content,expanding);else details.open=expanding;
  });
  reduced.addEventListener?.('change',()=>{if(reduced.matches)for(const [details,state] of active)clean(details,state)});
  window.addEventListener('pagehide',()=>{for(const [details,state] of active)clean(details,state)});
})();

// Give each product card a hover accent drawn from its own artwork photo.
(() => {
  const cardSelector = '.product-card-wrapper, media-gallery';
  const color = {
    sampleSize: 24,
    channelMax: 255,
    hueCircle: 360,
    hueSector: 60,
    hueBins: 12,
    redSectorCount: 6,
    minimumAlpha: 180,
    minimumBrightness: 0.12,
    maximumBrightness: 0.88,
    minimumSaturation: 0.11,
    minimumColorWeight: 3,
    saturationWeight: 2,
    minimumAccentLuminance: 68,
    maximumAccentLuminance: 154,
    luminanceWeights: [0.2126, 0.7152, 0.0722],
    fallbackAccent: [42, 77, 91],
  };
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = color.sampleSize;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  const processed = new WeakSet();
  const pending = new WeakSet();

  const chooseAccent = (image) => {
    if (!context) return null;
    try {
      context.clearRect(0, 0, color.sampleSize, color.sampleSize);
      context.drawImage(image, 0, 0, color.sampleSize, color.sampleSize);
      const pixels = context.getImageData(0, 0, color.sampleSize, color.sampleSize).data;
      const buckets = Array.from({ length: color.hueBins }, () => ({ weight: 0, red: 0, green: 0, blue: 0 }));
      const neutral = { weight: 0, red: 0, green: 0, blue: 0 };

      for (let index = 0; index < pixels.length; index += 4) {
        if (pixels[index + 3] < color.minimumAlpha) continue;
        const red = pixels[index];
        const green = pixels[index + 1];
        const blue = pixels[index + 2];
        const maximum = Math.max(red, green, blue);
        const minimum = Math.min(red, green, blue);
        const brightness = maximum / color.channelMax;
        if (brightness < color.minimumBrightness || brightness > color.maximumBrightness) continue;
        const chroma = maximum - minimum;
        const saturation = maximum ? chroma / maximum : 0;
        let bucket = neutral;
        let weight = 1;

        if (saturation >= color.minimumSaturation && chroma) {
          let hue;
          if (maximum === red) hue = ((green - blue) / chroma) % color.redSectorCount;
          else if (maximum === green) hue = (blue - red) / chroma + 2;
          else hue = (red - green) / chroma + 4;
          hue = (hue * color.hueSector + color.hueCircle) % color.hueCircle;
          bucket = buckets[Math.floor(hue / (color.hueCircle / color.hueBins))];
          weight += saturation * color.saturationWeight;
        }

        bucket.weight += weight;
        bucket.red += red * weight;
        bucket.green += green * weight;
        bucket.blue += blue * weight;
      }

      const strongest = buckets.reduce((best, bucket) => bucket.weight > best.weight ? bucket : best);
      const chosen = strongest.weight >= color.minimumColorWeight ? strongest : neutral;
      if (!chosen.weight) return null;
      const channels = [chosen.red, chosen.green, chosen.blue].map(value => value / chosen.weight);
      const luminance = channels.reduce((sum, value, index) => sum + value * color.luminanceWeights[index], 0);
      const adjustedLuminance = Math.max(color.minimumAccentLuminance, Math.min(color.maximumAccentLuminance, luminance));
      return channels.map(value => Math.round(Math.max(0, Math.min(color.channelMax, value + adjustedLuminance - luminance))));
    } catch (_) {
      return null;
    }
  };

  const artworkAccentByHandle = Object.freeze({
    'new-geometric-wood-art-19505': [142, 108, 80],
    'new-geometric-striped-wood-art-07253': [142, 108, 80],
    'new-geometric-wood-art-01671': [118, 87, 125],
  });
  const accentForArtwork = (card) => {
    const productUrl = card.matches('media-gallery')
      ? window.location.pathname
      : card.querySelector('a[href*="/products/"]')?.getAttribute('href');
    const handle = productUrl?.match(/\/products\/([^/?#]+)/)?.[1];
    return artworkAccentByHandle[handle];
  };
  const applyAccent = (card, image) => {
    if (processed.has(card)) return;
    const artworkAccent = accentForArtwork(card);
    const sampled = artworkAccent || chooseAccent(image);
    card.style.setProperty('--ew-card-accent-rgb', (sampled || color.fallbackAccent).join(', '));
    card.dataset.ewCardAccent = artworkAccent ? 'artwork' : sampled ? 'sampled' : 'fallback';
    processed.add(card);
    pending.delete(card);
  };

  const processCard = (card) => {
    if (processed.has(card) || pending.has(card)) return;
    if (accentForArtwork(card)) {
      applyAccent(card, null);
      return;
    }
    const image = card.querySelector('.card__media img, .product__media-item img');
    if (!image) return;
    if (image.complete) {
      applyAccent(card, image);
      return;
    }
    pending.add(card);
    image.addEventListener('load', () => applyAccent(card, image), { once: true });
    image.addEventListener('error', () => applyAccent(card, image), { once: true });
  };

  const scan = (root) => {
    if (root.matches?.(cardSelector)) processCard(root);
    root.querySelectorAll?.(cardSelector).forEach(processCard);
  };

  const start = () => {
    scan(document);
    new MutationObserver((changes) => {
      for (const change of changes) {
        for (const node of change.addedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE) scan(node);
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();

// Present legacy product descriptions with the same collapsible sections as Coastal Dream.
(() => {
  const selector = '.product__description';
  const introCount = 2;
  const separator = '⸻';
  const labels = new Map([
    ['MATERIALS', 'Materials'],
    ['DIMENSIONS', 'Dimensions'],
    ['MOUNTING', 'Mounting'],
    ['COLORS', 'Colors'],
    ['SHIPPING', 'Shipping'],
  ]);
  const sectionOrder = ['Materials', 'Dimensions', 'Mounting', 'Colors', 'Shipping', 'Details'];
  const textOf = (node) => node.textContent.replace(/\s+/g, ' ').trim();
  const firstText = (node) => node.firstChild?.nodeType === Node.TEXT_NODE
    ? node.firstChild.textContent.trim().toUpperCase() : '';
  const hasMaterialsLabel = (node) => firstText(node) === 'MATERIALS' && !!node.querySelector('br');

  const render = (description) => {
    if (description.querySelector('details.ew-product-accordion')) return;
    const nodes = Array.from(description.children);
    const remaining = nodes.slice(introCount);
    if (!remaining.some((node) => labels.has(textOf(node).toUpperCase()) || hasMaterialsLabel(node))) return;

    const sections = new Map();
    let current;
    const add = (title, node) => {
      const content = sections.get(title) || [];
      content.push(node);
      sections.set(title, content);
    };

    for (const node of remaining) {
      const text = textOf(node);
      if (!text || text === separator) {
        node.remove();
        continue;
      }
      const heading = labels.get(text.toUpperCase());
      if (heading) {
        current = heading;
        if (!sections.has(heading)) sections.set(heading, []);
        node.remove();
        continue;
      }
      if (hasMaterialsLabel(node)) {
        current = 'Materials';
        node.firstChild.textContent = node.firstChild.textContent.replace(/^\s*MATERIALS\s*/i, '');
        node.querySelector('br').remove();
        add(current, node);
        continue;
      }
      if (/\b(return|returns|right fit)\b/i.test(text)) {
        add('Shipping', node);
        continue;
      }
      add(current || 'Details', node);
    }

    for (const title of sectionOrder) {
      const content = sections.get(title);
      if (!content?.length) continue;
      const details = document.createElement('details');
      details.className = 'ew-product-accordion';
      const summary = document.createElement('summary');
      summary.textContent = title;
      const body = document.createElement('div');
      body.append(...content);
      details.append(summary, body);
      description.append(details);
    }
  };

  const scan = (root) => {
    if (root.matches?.(selector)) render(root);
    root.querySelectorAll?.(selector).forEach(render);
  };
  const start = () => {
    scan(document);
    new MutationObserver((records) => {
      for (const record of records) {
        record.target.closest?.(selector) && render(record.target.closest(selector));
        record.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) scan(node);
        });
      }
    }).observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();

/* Everwood additional refinements: search and contact. */
(() => {
  const config = Object.freeze({
    firstIndex: 0, step: 1, afterSubmitMs: 0,
    messageMinHeightPx: 120, messageMaxHeightPx: 360,
    blankSearch: 'Enter an artwork name or style to search.',
    emailRequired: 'Please enter your email address.',
    emailInvalid: 'Enter a valid email address so we can reply.',
    messageRequired: 'Please include a message so we can help.',
    sendingLabel: 'Sending…', messageLabel: 'Message *',
  });
  const searchHints = new WeakMap();
  const composingInputs = new WeakSet();
  let searchId = config.firstIndex;
  const describe = (field, id, add) => {
    const ids = new Set((field.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
    add ? ids.add(id) : ids.delete(id);
    ids.size ? field.setAttribute('aria-describedby', Array.from(ids).join(' ')) : field.removeAttribute('aria-describedby');
  };
  document.addEventListener('compositionstart', event => composingInputs.add(event.target), true);
  document.addEventListener('compositionend', event => composingInputs.delete(event.target), true);
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[role="search"]') || event.defaultPrevented) return;
    const query = form.querySelector('input[name="q"]');
    if (!query || composingInputs.has(query)) return;
    const term = query.value.trim();
    if (term) { query.value = term; return; }
    event.preventDefault();
    let hint = searchHints.get(form);
    if (!hint) {
      hint = document.createElement('p');
      hint.className = 'ew-search-hint';
      searchId += config.step;
      hint.id = 'ew-search-hint-' + searchId;
      hint.setAttribute('role', 'status');
      hint.textContent = config.blankSearch;
      form.append(hint);
      searchHints.set(form, hint);
    }
    hint.hidden = false;
    describe(query, hint.id, true);
    query.focus();
  }, true);
  document.addEventListener('input', event => {
    const query = event.target;
    if (!(query instanceof HTMLInputElement) || query.name !== 'q') return;
    const hint = searchHints.get(query.form);
    if (hint) { hint.hidden = true; describe(query, hint.id, false); }
  });

  function setupContact(form) {
    if (form.dataset.ewContactReady) return;
    const email = form.querySelector('input[type="email"]');
    const message = form.querySelector('textarea[name="contact[Comment]"]');
    const button = form.querySelector('button[type="submit"],input[type="submit"]');
    if (!email || !message || !button) return;
    form.dataset.ewContactReady = 'true';
    email.required = true;
    message.required = true;
    const label = form.querySelector('label[for="' + message.id + '"]');
    if (label) label.textContent = config.messageLabel;
    const errors = new Map();
    const previousInvalid = new Map();
    let pending = false, feedbackTimer, resizeFrame, feedbackSnapshot, invalidFocusFrame;
    const reset = () => {
      clearTimeout(feedbackTimer);
      pending = false;
      // Restore only the state this handler actually changed. A CAPTCHA may own its own feedback.
      if (!feedbackSnapshot) return;
      if (form.getAttribute('aria-busy') === 'true') feedbackSnapshot.busy === null ? form.removeAttribute('aria-busy') : form.setAttribute('aria-busy', feedbackSnapshot.busy);
      if (button.disabled) button.disabled = feedbackSnapshot.disabled;
      if (button instanceof HTMLInputElement) {
        if (button.value === config.sendingLabel) button.value = feedbackSnapshot.value;
      } else if (button.textContent === config.sendingLabel) button.replaceChildren(...feedbackSnapshot.nodes);
      feedbackSnapshot = null;
    };
    function clearError(field) {
      const error = errors.get(field);
      if (!error || error.hidden) return;
      error.hidden = true;
      describe(field, error.id, false);
      const previous = previousInvalid.get(field);
      previous == null ? field.removeAttribute('aria-invalid') : field.setAttribute('aria-invalid', previous);
      previousInvalid.delete(field);
    }
    function showError(field, text) {
      let error = errors.get(field);
      if (!error) {
        error = document.createElement('p');
        error.id = field.id + '-ew-error';
        error.className = 'ew-field-error';
        error.setAttribute('role', 'alert');
        field.closest('.field')?.insertAdjacentElement('afterend', error) || field.insertAdjacentElement('afterend', error);
        errors.set(field, error);
      }
      if (!previousInvalid.has(field)) previousInvalid.set(field, field.getAttribute('aria-invalid'));
      error.textContent = text;
      error.hidden = false;
      field.setAttribute('aria-invalid', 'true');
      describe(field, error.id, true);
    }
    const style = getComputedStyle(message);
    const minimumHeight = Math.min(config.messageMaxHeightPx, Math.max(config.messageMinHeightPx, message.offsetHeight));
    const borderHeight = (parseFloat(style.borderTopWidth) || config.firstIndex) + (parseFloat(style.borderBottomWidth) || config.firstIndex);
    const resize = () => {
      if (!message.isConnected || !message.getClientRects().length) return;
      message.style.height = 'auto';
      const needed = message.scrollHeight + borderHeight;
      message.style.height = Math.min(config.messageMaxHeightPx, Math.max(minimumHeight, needed)) + 'px';
      message.style.overflowY = needed > config.messageMaxHeightPx ? 'auto' : 'hidden';
    };
    const queueResize = () => {
      if (resizeFrame) return;
      resizeFrame = requestAnimationFrame(() => { resizeFrame = null; resize(); });
    };
    email.addEventListener('input', () => clearError(email));
    message.addEventListener('input', () => { clearError(message); queueResize(); });
    window.addEventListener('resize', queueResize, { passive: true });
    window.addEventListener('pageshow', () => { reset(); queueResize(); });
    resize();
    form.addEventListener('invalid', event => {
      const field = event.target;
      if (field !== email && field !== message) return;
      event.preventDefault();
      showError(field, field === email ? (email.value.trim() ? config.emailInvalid : config.emailRequired) : config.messageRequired);
      if (invalidFocusFrame) return;
      invalidFocusFrame = requestAnimationFrame(() => {
        invalidFocusFrame = null;
        [email, message].find(candidate => errors.get(candidate)?.hidden === false)?.focus();
      });
    }, true);
    form.addEventListener('submit', event => {
      if (event.defaultPrevented) return;
      if (pending) { event.preventDefault(); event.stopImmediatePropagation(); return; }
      clearError(email); clearError(message);
      if (!email.value.trim() || !email.validity.valid) showError(email, email.value.trim() ? config.emailInvalid : config.emailRequired);
      if (!message.value.trim()) showError(message, config.messageRequired);
      const invalid = [email, message].find(field => errors.get(field)?.hidden === false);
      if (invalid) { event.preventDefault(); event.stopPropagation(); invalid.focus(); return; }
      pending = true;
      feedbackTimer = setTimeout(() => {
        // Shopify's CAPTCHA or another app may cancel a valid submission.
        if (event.defaultPrevented) { reset(); return; }
        feedbackSnapshot = {busy: form.getAttribute('aria-busy'), disabled: button.disabled, value: button.value, nodes: Array.from(button.childNodes)};
        form.setAttribute('aria-busy', 'true');
        button.disabled = true;
        button instanceof HTMLInputElement ? button.value = config.sendingLabel : button.textContent = config.sendingLabel;
      }, config.afterSubmitMs);
    }, true);
  }
  const initialize = root => root.querySelectorAll('form#ContactForm').forEach(setupContact);
  initialize(document);
  document.addEventListener('shopify:section:load', event => initialize(event.target));
})();
